import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Contact from '../components/Contact';
import { ArrowRight, CheckCircle2, Home, Building2, Shield, Wrench, Users, Award, X, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const dachTranslations = {
  de: {
    heroTitle: 'Professionelle',
    heroTitle2: 'Dacharbeiten',
    heroDesc: 'Von der Dachsanierung bis zum Neubau – unser erfahrenes Team sorgt für ein sicheres und langlebiges Dach über Ihrem Kopf.',
    freeConsult: 'Kostenlose Beratung',
    services: 'Leistungen',
    completedProjects: 'Projekte abgeschlossen',
    yearsWarranty: 'Jahre Garantie',
    certMaster: 'Meisterbetrieb',
    ourServices: 'Unsere Leistungen',
    servicesDesc: 'Professionelle Dacharbeiten für jeden Bedarf',
    roofRenovation: 'Dachsanierung',
    roofRenovationDesc: 'Komplette Erneuerung und Sanierung Ihres Daches',
    newRoofing: 'Neueindeckung',
    newRoofingDesc: 'Hochwertige Dacheindeckungen für Neubauten',
    flatRoof: 'Flachdach',
    flatRoofDesc: 'Professionelle Flachdacharbeiten und Abdichtung',
    insulation: 'Dämmung',
    insulationDesc: 'Energieeffiziente Dachdämmung für besseren Wärmeschutz',
    ready: 'Bereit für Ihr Dachprojekt?',
    readyDesc: 'Kontaktieren Sie uns für eine kostenlose Beratung und ein unverbindliches Angebot.'
  },
  en: {
    heroTitle: 'Professional',
    heroTitle2: 'Roofing Work',
    heroDesc: 'From roof renovation to new construction – our experienced team ensures a safe and durable roof over your head.',
    freeConsult: 'Free Consultation',
    services: 'Services',
    completedProjects: 'Projects completed',
    yearsWarranty: 'Years Warranty',
    certMaster: 'Certified Master',
    ourServices: 'Our Services',
    servicesDesc: 'Professional roofing work for every need',
    roofRenovation: 'Roof Renovation',
    roofRenovationDesc: 'Complete renovation and repair of your roof',
    newRoofing: 'New Roofing',
    newRoofingDesc: 'High-quality roof covering for new buildings',
    flatRoof: 'Flat Roof',
    flatRoofDesc: 'Professional flat roof work and sealing',
    insulation: 'Insulation',
    insulationDesc: 'Energy-efficient roof insulation for better thermal protection',
    ready: 'Ready for Your Roofing Project?',
    readyDesc: 'Contact us for a free consultation and a non-binding quote.'
  }
};

const DachdeckerHero = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = dachTranslations[language] || dachTranslations.de;

  return (
    <section className="relative h-screen min-h-[600px] overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://customer-assets.emergentagent.com/job_solar-solutions-53/artifacts/b4f0xm50_39550a77-9fcd-467a-8bf3-d9fc1af67263.jpeg"
          alt="Dachdeckerei"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/85 via-slate-900/70 to-slate-900/60"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              {t.heroTitle}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-sky-400">
                {t.heroTitle2}
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-200 mb-8 leading-relaxed max-w-2xl">
              {t.heroDesc}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Button 
                onClick={() => navigate('/beratung')}
                size="lg"
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-8 py-6 text-lg group shadow-xl"
              >
                {t.freeConsult}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                onClick={() => document.getElementById('leistungen').scrollIntoView({ behavior: 'smooth' })}
                size="lg"
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg"
              >
                {t.services}
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">500+</div>
                <div className="text-sm text-gray-400">{t.completedProjects}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">10</div>
                <div className="text-sm text-gray-400">{t.yearsWarranty}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">100%</div>
                <div className="text-sm text-gray-400">{t.certMaster}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-white/70 rounded-full"></div>
        </div>
      </div>
    </section>
  );
};

// Photo Gallery Section
const DachdeckerGalerie = () => {
  const { language } = useLanguage();
  const [selectedImage, setSelectedImage] = useState(null);
  
  const photos = language === 'en' ? [
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/qh2ux214_IMG_0135.jpeg', title: 'Complete Roof Renovation', category: 'Renovation', description: 'Complete roof renovation with modern materials' },
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/05otj3e3_IMG_0134.jpeg', title: 'New Roofing', category: 'New Roofing', description: 'Professional new roofing for long-term protection' },
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/kvw4h89j_IMG_0133.jpeg', title: 'Roofing Work', category: 'Our Project', description: 'High-quality roofing work with precision' },
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/2m90p79v_IMG_0132.jpeg', title: 'Finished Roof', category: 'Completed', description: 'Quality work for satisfied customers' },
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/fq43eiub_IMG_0131.webp', title: 'Modern Roof Construction', category: 'New Build', description: 'Innovative roof constructions for modern buildings' },
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/3g30evhk_IMG_0130.jpeg', title: 'Roof Tile Installation', category: 'Craftsmanship', description: 'Precise installation of high-quality roof tiles' },
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/c6u86iot_IMG_0125.jpeg', title: 'Roofing Team', category: 'Our Team', description: 'Experienced professionals at work' },
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/85jwtsi2_IMG_0127.jpeg', title: 'Flat Roof Project', category: 'Flat Roof', description: 'Professional flat roof sealing' },
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/09g05yye_IMG_0126.jpeg', title: 'Tile Roofing', category: 'Roofing', description: 'Professional tile roofing' },
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/digbdbug_IMG_0128.webp', title: 'Roof Project', category: 'Project', description: 'More successful roofing work' }
  ] : [
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/qh2ux214_IMG_0135.jpeg', title: 'Dachsanierung komplett', category: 'Sanierung', description: 'Komplette Dachsanierung mit modernen Materialien' },
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/05otj3e3_IMG_0134.jpeg', title: 'Neueindeckung', category: 'Neueindeckung', description: 'Professionelle Neueindeckung für langjährigen Schutz' },
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/kvw4h89j_IMG_0133.jpeg', title: 'Dacharbeiten', category: 'Unser Projekt', description: 'Hochwertige Dacharbeiten mit Präzision' },
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/2m90p79v_IMG_0132.jpeg', title: 'Fertiggestelltes Dach', category: 'Fertig', description: 'Qualitätsarbeit für zufriedene Kunden' },
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/fq43eiub_IMG_0131.webp', title: 'Moderne Dachkonstruktion', category: 'Neubau', description: 'Innovative Dachkonstruktionen für moderne Gebäude' },
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/3g30evhk_IMG_0130.jpeg', title: 'Dachziegel Montage', category: 'Handwerk', description: 'Präzise Montage von hochwertigen Dachziegeln' },
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/c6u86iot_IMG_0125.jpeg', title: 'Dachdeckerteam', category: 'Unser Team', description: 'Erfahrene Fachkräfte bei der Arbeit' },
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/85jwtsi2_IMG_0127.jpeg', title: 'Flachdach Projekt', category: 'Flachdach', description: 'Professionelle Flachdachabdichtung' },
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/09g05yye_IMG_0126.jpeg', title: 'Ziegeleindeckung', category: 'Eindeckung', description: 'Fachgerechte Ziegeleindeckung' },
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/digbdbug_IMG_0128.webp', title: 'Dachprojekt', category: 'Projekt', description: 'Weitere erfolgreiche Dacharbeiten' }
  ];

  const galleryText = language === 'en' ? {
    title: 'Our',
    titleHighlight: 'Projects',
    subtitle: 'Discover our successfully completed roofing projects'
  } : {
    title: 'Unsere',
    titleHighlight: 'Projekte',
    subtitle: 'Entdecken Sie unsere erfolgreich abgeschlossenen Dachprojekte'
  };

  return (
    <section id="projekte" className="py-20 bg-gradient-to-br from-slate-900 to-sky-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            {galleryText.title} <span className="text-amber-400">{galleryText.titleHighlight}</span>
          </h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            {galleryText.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {photos.map((photo, index) => (
            <div 
              key={index} 
              className="group cursor-pointer relative overflow-hidden rounded-xl shadow-lg"
              onClick={() => setSelectedImage(photo)}
            >
              <div className="aspect-[4/3]">
                <img 
                  src={photo.src} 
                  alt={photo.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="text-amber-400 text-sm font-medium">{photo.category}</span>
                  <h3 className="text-white text-xl font-bold">{photo.title}</h3>
                  <p className="text-gray-300 text-sm mt-1">{photo.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Image Modal */}
        {selectedImage && (
          <div 
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <button className="absolute top-4 right-4 text-white hover:text-gray-300">
              <X className="w-8 h-8" />
            </button>
            <div className="max-w-4xl w-full" onClick={e => e.stopPropagation()}>
              <img 
                src={selectedImage.src} 
                alt={selectedImage.title}
                className="w-full h-auto rounded-xl"
              />
              <div className="text-center mt-4">
                <span className="text-amber-400 text-sm font-medium">{selectedImage.category}</span>
                <h3 className="text-white text-2xl font-bold">{selectedImage.title}</h3>
                <p className="text-gray-300">{selectedImage.description}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const DachdeckerLeistungen = () => {
  const { language } = useLanguage();
  
  const privatLeistungen = language === 'en' ? [
    { title: 'New Roofing', beschreibung: 'Complete new roofing of your roof with modern materials', features: ['Roof tiles', 'Roof stones', 'Slate', 'Bitumen shingles'] },
    { title: 'Roof Renovation', beschreibung: 'Professional renovation and modernization of existing roofs', features: ['Insulation', 'Sealing', 'Repairs', 'Energy renovation'] },
    { title: 'Flat Roof', beschreibung: 'Professional flat roof sealing with durable systems', features: ['Bitumen', 'Plastic', 'EPDM', 'Green roof'] },
    { title: 'Roof Windows', beschreibung: 'Installation and replacement of roof windows for more light', features: ['VELUX', 'FAKRO', 'Roto', 'Skylights'] }
  ] : [
    { title: 'Neueindeckung', beschreibung: 'Komplette Neueindeckung Ihres Daches mit modernen Materialien', features: ['Dachziegel', 'Dachsteine', 'Schiefer', 'Bitumenschindeln'] },
    { title: 'Dachsanierung', beschreibung: 'Fachgerechte Sanierung und Modernisierung bestehender Dächer', features: ['Dämmung', 'Abdichtung', 'Reparaturen', 'Energetische Sanierung'] },
    { title: 'Flachdach', beschreibung: 'Professionelle Flachdachabdichtung mit langlebigen Systemen', features: ['Bitumen', 'Kunststoff', 'EPDM', 'Gründach'] },
    { title: 'Dachfenster', beschreibung: 'Einbau und Austausch von Dachfenstern für mehr Licht', features: ['VELUX', 'FAKRO', 'Roto', 'Dachflächenfenster'] }
  ];

  const gewerbeLeistungen = language === 'en' ? [
    { title: 'Industrial Roofs', beschreibung: 'Large roof areas for industrial and commercial buildings', features: ['Trapezoidal sheet', 'Sandwich panels', 'Light strips', 'Maintenance contracts'] },
    { title: 'Hall Roofs', beschreibung: 'Specialized in wide-span hall roof constructions', features: ['Steel trapezoidal profiles', 'Insulation', 'Smoke extraction', 'Lightning protection'] },
    { title: 'Commercial Flat Roofs', beschreibung: 'Robust flat roof systems for commercial properties', features: ['Walkable', 'Driveable', 'Green roof', 'PV mounting'] },
    { title: 'Roof Safety', beschreibung: 'Fall protection and safety equipment', features: ['Cable systems', 'Anchor points', 'Walkways', 'Snow guard systems'] }
  ] : [
    { title: 'Industriedächer', beschreibung: 'Große Dachflächen für Industrie- und Gewerbehallen', features: ['Trapezblech', 'Sandwichpaneele', 'Lichtbänder', 'Wartungsverträge'] },
    { title: 'Hallendächer', beschreibung: 'Spezialisiert auf weitspannende Hallendachkonstruktionen', features: ['Stahltrapezprofile', 'Isolierung', 'RWA-Anlagen', 'Blitzschutz'] },
    { title: 'Gewerbliche Flachdächer', beschreibung: 'Robuste Flachdachsysteme für gewerbliche Immobilien', features: ['Begehbar', 'Befahrbar', 'Dachbegrünung', 'PV-Montage'] },
    { title: 'Dachsicherheit', beschreibung: 'Absturzsicherungen und Sicherheitseinrichtungen', features: ['Seilsysteme', 'Anschlagpunkte', 'Laufstege', 'Schneefangsysteme'] }
  ];

  return (
    <section id="leistungen" className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Private Kunden */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-sky-100 rounded-full mb-4">
              <Home className="w-8 h-8 text-sky-600" />
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {language === 'en' ? 'For ' : 'Für '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-amber-500">{language === 'en' ? 'Private Customers' : 'Privatkunden'}</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {language === 'en' ? 'Your roof in the best hands – from consultation to completion' : 'Ihr Dach in besten Händen – von der Beratung bis zur Fertigstellung'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {privatLeistungen.map((leistung, index) => (
              <Card key={index} className="border-2 border-gray-100 hover:border-sky-200 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{leistung.title}</h3>
                  <p className="text-gray-600 mb-4 text-sm">{leistung.beschreibung}</p>
                  <ul className="space-y-2">
                    {leistung.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-700">
                        <CheckCircle2 className="w-4 h-4 text-sky-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Gewerbekunden */}
        <div>
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
              <Building2 className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {language === 'en' ? 'For ' : 'Für '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-amber-500">{language === 'en' ? 'Commercial Customers' : 'Gewerbekunden'}</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {language === 'en' ? 'Large projects, professional execution – reliable and on schedule' : 'Große Projekte, professionelle Ausführung – zuverlässig und termingerecht'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {gewerbeLeistungen.map((leistung, index) => (
              <Card key={index} className="border-2 border-gray-100 hover:border-amber-200 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{leistung.title}</h3>
                  <p className="text-gray-600 mb-4 text-sm">{leistung.beschreibung}</p>
                  <ul className="space-y-2">
                    {leistung.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-700">
                        <CheckCircle2 className="w-4 h-4 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const DachdeckerVorteile = () => {
  const { language } = useLanguage();
  
  const vorteile = language === 'en' ? [
    {
      icon: Shield,
      title: 'Certified Master',
      description: 'Certified master craftsman with years of experience and expertise'
    },
    {
      icon: Award,
      title: 'Quality Guarantee',
      description: 'We guarantee the highest quality and use only first-class materials'
    },
    {
      icon: Wrench,
      title: 'Complete Service',
      description: 'From planning to execution to maintenance – everything from one source'
    },
    {
      icon: Users,
      title: 'Experienced Team',
      description: 'Qualified professionals with years of experience in roofing'
    },
    {
      icon: Shield,
      title: 'Insured',
      description: 'Fully insured for your safety and our professional service'
    },
    {
      icon: CheckCircle2,
      title: 'On Schedule',
      description: 'Reliable and punctual execution of all projects on schedule'
    }
  ] : [
    {
      icon: Shield,
      title: 'Meisterbetrieb',
      description: 'Zertifizierter Meisterbetrieb mit langjähriger Erfahrung und Fachkompetenz'
    },
    {
      icon: Award,
      title: 'Qualitätsgarantie',
      description: 'Wir garantieren höchste Qualität und verwenden nur erstklassige Materialien'
    },
    {
      icon: Wrench,
      title: 'Komplettservice',
      description: 'Von der Planung über die Ausführung bis zur Wartung – alles aus einer Hand'
    },
    {
      icon: Users,
      title: 'Erfahrenes Team',
      description: 'Qualifizierte Fachkräfte mit langjähriger Erfahrung im Dachdeckerhandwerk'
    },
    {
      icon: Shield,
      title: 'Versichert',
      description: 'Vollständig versichert für Ihre Sicherheit und unseren professionellen Service'
    },
    {
      icon: CheckCircle2,
      title: 'Termingerecht',
      description: 'Zuverlässige und pünktliche Ausführung aller Projekte nach Zeitplan'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {language === 'en' ? 'Why ' : 'Warum '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-amber-500">Gravante?</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {language === 'en' ? 'Your reliable partner for all roofing work' : 'Ihr zuverlässiger Partner für alle Dacharbeiten'}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {vorteile.map((vorteil, index) => {
            const Icon = vorteil.icon;
            return (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-0 bg-white hover:-translate-y-2">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-sky-100 to-amber-100 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-8 h-8 text-sky-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{vorteil.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{vorteil.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Materialien = () => {
  const { language } = useLanguage();
  
  const materialien = language === 'en' ? [
    { name: 'Roof Tiles', beschreibung: 'Classic and durable' },
    { name: 'Roof Stones', beschreibung: 'Modern and versatile' },
    { name: 'Slate', beschreibung: 'Noble and timeless' },
    { name: 'Bitumen', beschreibung: 'For flat roofs' },
    { name: 'Trapezoidal Sheet', beschreibung: 'For industrial roofs' },
    { name: 'EPDM', beschreibung: 'Flexible sealing' }
  ] : [
    { name: 'Dachziegel', beschreibung: 'Klassisch und langlebig' },
    { name: 'Dachsteine', beschreibung: 'Modern und vielseitig' },
    { name: 'Schiefer', beschreibung: 'Edel und zeitlos' },
    { name: 'Bitumen', beschreibung: 'Für Flachdächer' },
    { name: 'Trapezblech', beschreibung: 'Für Industriedächer' },
    { name: 'EPDM', beschreibung: 'Flexible Abdichtung' }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {language === 'en' ? 'High-Quality ' : 'Hochwertige '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-amber-500">{language === 'en' ? 'Materials' : 'Materialien'}</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {language === 'en' ? 'We only work with first-class materials from renowned manufacturers' : 'Wir arbeiten nur mit erstklassigen Materialien namhafter Hersteller'}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {materialien.map((material, index) => (
            <div key={index} className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-2">{material.name}</h3>
              <p className="text-gray-600">{material.beschreibung}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const DachdeckerCTA = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  return (
    <section className="py-20 bg-gradient-to-r from-sky-600 to-amber-500">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            {language === 'en' ? 'Planning a Roofing Project?' : 'Dachprojekt geplant?'}
          </h2>
          <p className="text-xl mb-8 leading-relaxed opacity-90">
            {language === 'en' ? 'Contact us for a free consultation and a non-binding quote.' : 'Kontaktieren Sie uns für eine kostenlose Beratung und ein unverbindliches Angebot.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/beratung')}
              size="lg"
              className="bg-white text-sky-600 hover:bg-gray-100 px-8 py-6 text-lg font-bold shadow-xl"
            >
              {language === 'en' ? 'Request Now' : 'Jetzt anfragen'}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <a 
              href="tel:+4915782283963"
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-lg text-lg font-medium transition-colors"
            >
              <Phone className="w-5 h-5" />
              +49 157 82283963
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

const DachdeckerPage = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <DachdeckerHero />
      <DachdeckerGalerie />
      <DachdeckerLeistungen />
      <DachdeckerVorteile />
      <Materialien />
      <DachdeckerCTA />
      <Contact />
      <Footer />
    </div>
  );
};

export default DachdeckerPage;
